import { db, auth } from '@/lib/firebase';
import { collection, addDoc, query, where, getDocs, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';

export interface AnalysisRecord {
  id?: string;
  userId: string;
  timestamp: Timestamp | Date; // Firestore Timestamp on write, Date on read
  analysisType: string; // e.g., "Text Analysis", "Domain WHOIS", "Image Geolocation", "Document Analysis", "Search Operator Query"
  inputSummary: string; // e.g., first 50 chars of text, domain name, image file name, doc file name, generated query
  parameters?: Record<string, any>; // e.g., { platform: "Google", searchType: "site_specific", keywords: "AI", site: "example.com" }
  resultSummary?: string; // e.g., first 100 chars of AI response, "WHOIS data fetched", "Location identified", "Copied to clipboard"
  // We can add fullInput and fullResult if needed, but be mindful of Firestore document size limits
}

const HISTORY_COLLECTION = 'userAnalysisHistory';

/**
 * Adds an analysis record to the user's history in Firestore.
 */
export const addAnalysisToHistory = async (analysisData: Omit<AnalysisRecord, 'id' | 'timestamp'>): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('No user logged in. Cannot save analysis to history.');
    return null;
  }

  try {
    const docRef = await addDoc(collection(db, HISTORY_COLLECTION), {
      ...analysisData,
      userId: user.uid, // Ensure userId is the current user's UID
      timestamp: serverTimestamp(),
    });
    console.log('Analysis record added with ID: ', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error adding analysis record to Firestore: ', error);
    // Consider how to handle this error in the UI, e.g., toast notification
    return null;
  }
};

/**
 * Retrieves all analysis records for the currently logged-in user.
 */
export const getUserAnalysisHistory = async (): Promise<AnalysisRecord[]> => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('No user logged in. Cannot fetch analysis history.');
    return [];
  }

  try {
    const q = query(
      collection(db, HISTORY_COLLECTION),
      where('userId', '==', user.uid),
      orderBy('timestamp', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const history: AnalysisRecord[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      history.push({
        id: doc.id,
        ...data,
        timestamp: (data.timestamp as Timestamp)?.toDate ? (data.timestamp as Timestamp).toDate() : new Date(), // Convert Firestore Timestamp to JS Date
      } as AnalysisRecord);
    });
    return history;
  } catch (error) {
    console.error('Error fetching user analysis history: ', error);
    return [];
  }
};
